/** The random map generator.
 *
 * `generateMap(seed, opts)` is a pure function of its arguments: every roll
 * comes from the `map.generation` PCG32 stream seeded from `seed`, the data it
 * reads is a `ContentRegistry`, and it touches no clock, no disk and no
 * `Math.random`. The same seed produces byte-identical terrain, the same object
 * list in the same order, and the same object ids.
 *
 * Object ids are minted as `object:1`, `object:2`, ... which is exactly what
 * `mintObjectId` produces. Hand `objects` to `newGame` in the order this
 * returns them - `toObjectSetups` does that - and the ids in the finished
 * `GameState` are the ids in this result.
 *
 * The order of the passes is the design:
 *
 *   zones -> terrain -> rivers -> town sites -> neutral towns -> ridges ->
 *   roads -> objects -> gate mouths -> spur roads -> decoration -> audit ->
 *   underground -> assemble
 *
 * Terrain has to exist before a town can be sited on it; town sites have to be
 * known before ridges are raised, or a ridge walls a town in; roads have to be
 * laid before objects, or every object sits where the road wants to go; and the
 * audit runs last because it is the only pass allowed the final word.
 *
 * The first four of those are one unit - `buildLayout` - and it is run again
 * from scratch as often as it takes to seat the capitals a real march apart.
 * Nothing downstream can fix a layout that puts two starts inside each other's
 * opening move: the terrain under a start was chosen for that start, so the
 * only honest repair is another layout. `requiredSeparation` is the bar and
 * `startSeparation` is the measurement.
 *
 * The second level is generated as a *separate one-level map* and merged in at
 * the end. Every pass in this package addresses a cell as `y * width + x`, and
 * making them all z-aware to build one two-level array would have touched every
 * loop in the package for no gain: a cave is carved out of rock rather than
 * grown out of zones, so it shares no pass with the surface anyway. `assemble`
 * copies the finished planes into one `levels: 2` map and re-stamps every
 * object footprint onto it, which is also what keeps `moveCost` derived rather
 * than copied.
 *
 * This file is the pipeline and the layout pass; the other passes are sibling
 * modules, each below this one in the import graph: `siting.ts` (town sites),
 * `roads.ts`, `placement.ts` (draws, retries, payloads, guards, renumbering),
 * `populate.ts` (the surface object pass), `neutralTowns.ts`, `gates.ts`,
 * `audit.ts`, `underworld.ts`; the result types are in `types.ts`. Everything
 * they export that used to be exported from here still is, via re-export.
 */

import type {
  AdventureMapState, ContentRegistry, FactionId, MapObjectState, ObjectSetup, TerrainKind,
} from "@game/core";
import {
  RIVER_KINDS, ROAD_KINDS, TERRAINS, createMap, indexOf, isWaterCell, setOccupant, setRiver,
  setRoad, setTerrain, setVariant,
} from "@game/core";

import type { Grid } from "./grid.js";
import { allNeighbours, idx, inside, terrainOf, walkable, xOf, yOf } from "./grid.js";
import type { WaterShape, Zone } from "./biomes.js";
import {
  assignTerrain, assignZoneJobs, despeckle, erodeBoundaries, growZones, paintBeaches, paintZones,
  setVariants, shapesFor, smoothSeams, startRing, zoneAdjacency, zoneMap,
} from "./biomes.js";
import { carveRivers, raiseRidges, repairConnectivity, repairRivers } from "./rivers.js";
import { carveChannels } from "./waterways.js";
import { OPEN_APRON, decalScale, thicketScale } from "./landform.js";
import { DELTA_DECAL, FIELD_DECALS, SCENERY } from "./catalog.js";
import { assignZoneThemes } from "./pools.js";
import { dressRidges } from "./obstacleWalls.js";
import { carveLabyrinth } from "./maze.js";
import { plantWoodWalls } from "./woodWalls.js";
import { growThickets } from "./thickets.js";
import type { PlaceContext } from "./place.js";
import { objectIdFor, tryPlaceAt } from "./place.js";
import type { Rng } from "./rng.js";
import { int, makeRng, makeSideRng, pick, shuffle } from "./rng.js";
import type { GenState } from "./state.js";
import { clamp } from "./state.js";
import type { GeneratedMap, GeneratedTown, MapGenOptions, StartingPosition } from "./types.js";
import type { Decoration } from "./types.js";
import type { TownSite } from "./siting.js";
import { drySite, findTownSite, heroCell, reserveAround, townDefFor, townFactions } from "./siting.js";
import { dangerField, nothing, place } from "./placement.js";
import { fillDiamonds, layTrunkRoads, laySpurRoads, pruneRoads } from "./roads.js";
import {
  buildVault, guardTheGates, linkOneWayPortals, linkPortals, linkWhirlpools, placeQuests,
  populateHomeZones, populateNeutralZones,
} from "./populate.js";
import { placeNeutralTowns, startingGarrison } from "./neutralTowns.js";
import { gatePairs, openGateMouths } from "./gates.js";
import { auditReachability } from "./audit.js";
import { buildUnderworld } from "./underworld.js";

// ------------------------------------------------------------------- options

// The option and result types live in `types.ts` (a leaf, so the pass modules
// can name them); they are re-exported here so nothing importing this module
// has to know that.
export type {
  Decoration, GateMouth, GatePair, GeneratedMap, GeneratedTown, MapGenOptions,
  StartingPosition,
} from "./types.js";
export { START_GARRISON_VALUE, START_GARRISON_WEEKS, startingGarrison } from "./neutralTowns.js";
export { townFactions } from "./siting.js";

const DEFAULTS = {
  width: 72, height: 72, players: 2,
  waterBp: 1800, monsterStrengthBp: 10000, decorationBp: 900, sceneryBp: 320,
  mazeBp: 4500,
} as const;

// --------------------------------------------------------------------- entry

export function generateMap(seed: number, opts: MapGenOptions): GeneratedMap {
  const content = opts.content;
  // 288 and not 252: a Massive map is 82,944 cells, and the generator builds
  // one in about seven seconds. It could not before - the same board took
  // three quarters of a minute until the connectivity flood in `place.ts` and
  // `grid.ts` was made to cost what it should - and a size nobody would wait
  // for is not a size worth offering.
  const width = clamp(opts.width ?? DEFAULTS.width, 24, 288);
  const height = clamp(opts.height ?? DEFAULTS.height, 24, 288);
  const players = clamp(opts.players ?? DEFAULTS.players, 2, 8);
  const waterBp = clamp(opts.waterBp ?? DEFAULTS.waterBp, 0, 6000);
  const monsterBp = clamp(opts.monsterStrengthBp ?? DEFAULTS.monsterStrengthBp, 1000, 40000);
  const decorationBp = clamp(opts.decorationBp ?? DEFAULTS.decorationBp, 0, 4000);
  const sceneryBp = clamp(opts.sceneryBp ?? DEFAULTS.sceneryBp, 0, 2000);
  const mazeBp = clamp(opts.mazeBp ?? DEFAULTS.mazeBp, 0, 10000);
  const wantUnderground = opts.underground ?? true;
  const wantNeutralTowns = clamp(
    opts.neutralTowns ?? Math.max(1, Math.round(players / 2)), 0, 8);

  // Two independent floors, both read off the ARGUMENTS rather than the roll,
  // so an impossible request fails the same way every time instead of failing
  // on one seed in four.
  //
  // The size floor is the binding one. The cells-per-player floor below it is
  // currently UNREACHABLE - `players` is clamped to 8 and the smallest map is
  // 48x48, which is 288 cells each against a 150 bar - and it is kept anyway,
  // because it guards the case where MIN_MAP_CELLS is lowered again and
  // because it is the rule that actually explains what goes wrong when a map
  // is too crowded. It has no test for the same reason it has no callers: a
  // test would have to defeat the clamp to reach it.
  if (width < MIN_MAP_CELLS || height < MIN_MAP_CELLS) {
    throw new Error(
      `mapgen: ${String(width)}x${String(height)} is below the ${String(MIN_MAP_CELLS)}-cell `
      + `minimum; a smaller map decides itself in the opening (see MIN_MAP_CELLS)`);
  }

  if (width * height < players * MIN_CELLS_PER_PLAYER) {
    throw new Error(
      `mapgen: ${String(width)}x${String(height)} has no room for `
      + `${String(players)} players (${String(MIN_CELLS_PER_PLAYER)} cells each `
      + `is the minimum)`);
  }

  const r = makeRng(seed);
  const side = makeSideRng(seed);
  const g: Grid = { width, height };

  // Drawn from the seed when the caller does not care, so consecutive maps are
  // different BOARDS and not the same board reshuffled. Drawn first, before any
  // other roll, so that a template pinning the shape leaves the rest of the
  // stream where it was.
  const shapes = shapesFor(width * height, players);
  const waterShape = opts.waterShape ?? shapes[int(r, shapes.length)]!;

  // ---- 1. factions, so the player zones already know what terrain they want
  const factions = chooseFactions(r, content, players, opts.factions);
  const nativeOf = factions.map((f) => nativeTerrain(content, f));

  // ---- 2-4. zones, rivers and town sites, as one attempt that is thrown away
  // whole if the capitals come out inside each other's opening march. Each
  // retry draws from a stream that has already moved on, so it is a different
  // rotation of the start ring and a different flood - not the same layout
  // reconsidered. See `requiredSeparation` for what "far enough" means.
  const need = requiredSeparation(g, players);
  let layout: Layout | null = null;
  for (let attempt = 0; attempt < LAYOUT_ATTEMPTS && layout === null; attempt++) {
    const candidate = buildLayout(g, r, players, waterBp, nativeOf, waterShape);
    if (candidate.separation >= need) layout = candidate;
  }
  if (layout === null) {
    // Refusing to return is the point. A map that hands one player the other's
    // capital on day two is not a harder map, it is a decided one, and shipping
    // it silently is how the whole headless suite came to end on day 4.
    throw new Error(
      `mapgen: seed ${String(seed)} cannot seat ${String(players)} starts `
      + `${String(need)} steps apart on ${String(width)}x${String(height)} `
      + `in ${String(LAYOUT_ATTEMPTS)} attempts`);
  }
  const { map, zones, zoneOf, adjacency, playerZones, sites } = layout;
  assignZoneThemes(zones, content, factions, r);

  const reserved = new Uint8Array(width * height);

  const protectedCells = new Set<number>();
  for (const s of sites) {
    if (s === null) continue;
    for (let dy = -6; dy <= 4; dy++) {
      for (let dx = -3; dx <= 9; dx++) {
        if (inside(g, s.x + dx, s.y + dy)) protectedCells.add(idx(g, s.x + dx, s.y + dy));
      }
    }
  }

  // ---- 5. ridges: a zone border becomes a wall with one or two gates
  const terrainBefore: Record<number, number> = {};
  for (let i = 0; i < map.terrain.length; i++) terrainBefore[i] = map.terrain[i]!;
  const ridge = raiseRidges(map, r, zoneOf, adjacency, protectedCells, 7000);

  // ---- 5.5 the labyrinth: the same wall-with-a-gap idea one level down, so a
  // zone is chambers to thread rather than a field to cross (maze.ts). It runs
  // HERE, before the repair below, because rock is the only wall the repair
  // understands and the whole land mass has to come out as one piece: the
  // reachability audit does not open walls, it deletes what it cannot reach.
  const maze = carveLabyrinth(map, r, zones, zoneOf, protectedCells, mazeBp);

  const mustConnect: number[] = [];
  for (const s of sites) if (s !== null) mustConnect.push(idx(g, s.entX, s.entY));
  for (const z of zones) if (z.terrain !== "water" && walkable(map, z.seed)) mustConnect.push(z.seed);
  // Every chamber the labyrinth cut, so the repair guarantees them the way it
  // already guarantees the zones. See `LabyrinthResult.chambers`.
  for (const c of maze.chambers) if (walkable(map, c)) mustConnect.push(c);
  // The town footprints are not on the map yet, and a repair that threads
  // its line through one is undone the moment the town is stamped: on a
  // 48-cell map with eight players the sixth town's own doorstep was joined
  // to the mainland straight through its walls, and the town was refused.
  // So the footprints are closed to the repair - all but the doorstep, which
  // is what has to be connected.
  const footprints = new Set<number>();
  for (const site of sites) {
    if (site === null) continue;
    for (let dy = -3; dy <= 0; dy++) {
      for (let dx = 0; dx <= 5; dx++) {
        const cx = site.x + dx, cy = site.y + dy;
        if (!inside(g, cx, cy) || (cx === site.entX && cy === site.entY)) continue;
        footprints.add(idx(g, cx, cy));
      }
    }
  }
  repairConnectivity(map, mustConnect, terrainBefore, true, footprints);
  // The two repair passes are allowed to put sand and old terrain back down
  // wherever they had to cut through, so the seam rule is re-applied last.
  smoothSeams(map);
  paintBeaches(map);
  setVariants(map);
  // ...and a river whose sea became a sandbar is walked on to water or erased.
  let rivers = repairRivers(map, layout.rivers, protectedCells);

  // ---- 6. objects; towns first, so `object:1..P` are the towns
  const ctx: PlaceContext = {
    map, content, z: 0, reserved, anchors: [],
    scratch: new Uint8Array(width * height),
  };
  const s: GenState = {
    r, side, map, g, content, ctx,
    objects: [], placements: [],
    danger: new Int32Array(width * height), monsterBp, zoneOf, zones,
    idBase: 0,
  };

  const towns: GeneratedTown[] = [];
  const startingPositions: StartingPosition[] = [];
  sites.forEach((first, p) => {
    const faction = factions[p]!;
    const defId = townDefFor(content, faction);
    const id = objectIdFor(s.objects.length + 1);
    // The site was chosen before the ridges, the repair and the other towns
    // existed, and any of them can make it unusable now - most often a
    // footprint that would cut an earlier town off the sandbar the repair
    // built. A player without a town is not a map, so the search is repeated
    // on the finished ground, avoiding the block that failed.
    let site = first;
    let res: ReturnType<typeof tryPlaceAt> = null;
    const avoid = new Set<number>();
    const zone = playerZones[p]!;
    for (let attempt = 0; attempt < 6 && site !== null && res === null; attempt++) {
      rivers = drySite(map, g, site, rivers);
      res = tryPlaceAt(ctx, defId, site.x, site.y, id, {}, null);
      if (res !== null) break;
      for (let dy = -3; dy <= 0; dy++) {
        for (let dx = 0; dx <= 5; dx++) {
          if (inside(g, site.x + dx, site.y + dy)) avoid.add(idx(g, site.x + dx, site.y + dy));
        }
      }
      site = findTownSite(map, r, zone, zoneOf, avoid);
    }
    if (res === null || site === null) return;
    s.objects.push(res.state);
    s.placements.push(res.placement);
    reserveAround(reserved, g, site.entX, site.entY, 1);
    towns.push({
      player: p, faction, def: defId,
      x: site.entX, y: site.entY, z: 0, object: s.objects.length - 1,
      garrison: startingGarrison(content, faction), built: [],
    });
    const hero = heroCell(map, g, site.entX, site.entY);
    startingPositions.push({
      player: p, faction, town: towns.length - 1,
      heroX: hero[0], heroY: hero[1], heroZ: 0,
    });
  });

  const playerTownCells = towns.map((t) => idx(g, t.x, t.y));

  // ---- 7. neutral towns, before the roads so the trunk network links them.
  // The danger field is a plain BFS over walkable cells and roads do not
  // change walkability, so computing it here gives the same answer as step 8.
  s.danger.set(dangerField(map, g, playerTownCells));
  placeNeutralTowns(s, wantNeutralTowns, zoneOf, towns, reserved, 0, 120);

  const townCells = towns.map((t) => idx(g, t.x, t.y));

  // ---- 8. roads: a spanning tree over the towns, then spurs to what matters
  layTrunkRoads(map, g, townCells, towns.map((t) => t.player >= 0));

  // ---- 9. the object pass
  populateHomeZones(s, playerZones, sites);
  populateNeutralZones(s);
  linkPortals(s);
  linkWhirlpools(s);
  linkOneWayPortals(s);
  placeQuests(s);
  // Every chokepoint is worth a guard, and a chamber gate is a chokepoint in
  // exactly the way a ridge gate is.
  guardTheGates(s, [...ridge.gates, ...maze.gates]);

  // ---- 10. gate mouths, before the audit so they are audited like anything
  // else: a gate a hero cannot walk to is a gate that does not exist.
  const channels = wantUnderground ? openGateMouths(s, 2 + int(r, 3)) : [];

  // ---- 11. spur roads to the things worth walking to twice, then cut the
  // network back to what it exists to join: no loops, no stubs in open grass.
  const spurred = laySpurRoads(s);
  pruneRoads(map, g, [...townCells, ...spurred]);
  fillDiamonds(map, g);

  // ---- 11.5 the vault, AFTER the roads it has to keep away from.
  //
  // It used to be built with the rest of the object pass, two steps before the
  // spur roads were laid - so its ring of blocking scenery was in the ground
  // before there was a road to be too close to, and a spur laid afterwards
  // could run straight along the ring's verge. That is scrub on a road's verge,
  // which `decorate` takes care never to produce and
  // `mapgen-landform.spec.ts` asserts, and it showed up the moment the
  // labyrinth moved the road network. Built here the site test can see the
  // finished network, which is what makes its road check mean anything.
  //
  // Nothing is lost by the move: `laySpurRoads` only spurs to mines, dwellings
  // and garrisons, so no road was ever laid to the vault anyway - and a paved
  // road up to a hidden treasure cache would be the wrong look for it.
  buildVault(s);

  // ---- 12. decoration, which never touches the occupancy plane
  const decorations = decorate(s, decorationBp, sceneryBp, rivers);

  // ---- 12.5 the walls get their faces. The wooded ones first (woodWalls.ts):
  // they put the ground back and grow forest where the rock was, and what they
  // leave behind is still rock, so `dressRidges` picks it up as a range without
  // either pass having to know about the other.
  plantWoodWalls(s, maze.wooded);
  // The river paths go along so a river that crosses a wall is cut at the
  // range (obstacleWalls.ts `cutRivers`); the lists are shortened in place.
  dressRidges(s, rivers);

  // ---- 12.6 thickets: woods thick enough to walk round rather than through
  // (thickets.ts). Blocking, so before the audit and never after it.
  growThickets(s);

  // ---- 13. the audit, with the kernel's own pathfinder
  auditReachability(s, playerTownCells.slice(0, 1));

  // ---- 14. the second level, and the gate pairing that makes it reachable
  const below = wantUnderground
    ? buildUnderworld(s, r, channels, monsterBp, decorationBp, sceneryBp)
    : null;

  const levels = below === null ? 1 : 2;
  const objects = below === null ? s.objects : [...s.objects, ...below.objects];
  const world = below === null
    ? map
    : assemble(width, height, [map, below.state.map], objects, content);

  // ---- 15. Section 31: the magic ground, painted last
  //
  // Last because it is a wash and not a layer anything else reads: no path
  // costs it, no placement avoids it, and the audit above has already proved
  // the map is walkable. Painting it earlier would put a patch under a town
  // site that moves afterwards.
  paintMagicGround(world, r, content);

  return {
    seed, width, height, levels, map: world,
    objects,
    towns: locateTowns(below === null ? towns : [...towns, ...below.towns], objects, content),
    startingPositions,
    decorations: below === null ? decorations : [...decorations, ...below.decorations],
    gates: gatePairs(objects),
    zoneOf,
    zoneJobs: zones.map((z) => z.job),
    rivers,
  };
}

/** Feed a generated map straight into `newGame`; ids line up with `objects`. */
export function toObjectSetups(gen: GeneratedMap): ObjectSetup[] {
  return gen.objects.map((o) => ({
    kind: o.kind, def: o.def, x: o.x, y: o.y, z: o.z,
    owner: o.owner, payload: o.payload,
  }));
}

// ------------------------------------------------------------------ internals

// `GenState` itself lives in `state.js`; see the note there for why. Re-exported
// so the pipeline still reads as its owner.
export type { GenState };


function chooseFactions(
  r: Rng, content: ContentRegistry, players: number, given: readonly FactionId[] | undefined,
): FactionId[] {
  const usable = townFactions(content);
  const fallback = usable[0] ?? ("sunspire" as FactionId);
  if (given !== undefined && given.length > 0) {
    return Array.from({ length: players }, (_, i) => given[i % given.length] ?? fallback);
  }
  const pool = shuffle(r, usable.slice());
  return Array.from({ length: players }, (_, i) => pool[i % Math.max(1, pool.length)] ?? fallback);
}

function nativeTerrain(content: ContentRegistry, f: FactionId): TerrainKind {
  const t = content.factions.get(f)?.nativeTerrain ?? "grass";
  return t === "water" || t === "rock" ? "grass" : t;
}

// -------------------------------------------------------------- the layout

/**
 * Everything the rest of the generator needs from the passes that decide where
 * the players stand: the painted terrain, the zones, the rivers, and one town
 * site per player. It is one value because it is accepted or thrown away as
 * one - a start position cannot be moved after the terrain under it was chosen
 * for it, so the only honest way to fix a bad layout is to build another.
 */
interface Layout {
  readonly map: AdventureMapState;
  readonly zones: Zone[];
  readonly zoneOf: Int32Array;
  readonly adjacency: number[][];
  readonly rivers: number[][];
  readonly playerZones: Zone[];
  readonly sites: (TownSite | null)[];
  /** Walking steps between the two closest capitals; 0 if the layout is broken. */
  readonly separation: number;
}

/**
 * How many layouts to try before giving up on the map seed entirely.
 *
 * A layout costs a zone flood, a river pass and a connectivity repair - single
 * -digit milliseconds on a small map - so a generous budget is cheap, and the
 * measured rejection rate is under one attempt in three.
 */
const LAYOUT_ATTEMPTS = 24;

/**
 * Below this much room per player the map cannot seat its capitals apart at
 * all: eight starts on a 24x24 map get 72 cells each, and a town footprint
 * plus its apron is a quarter of that, so two capitals end up five steps from
 * each other whatever the layout does.
 */
const MIN_CELLS_PER_PLAYER = 150;

/**
 * The floor under one side of the map, whatever the player count.
 *
 * MIN_CELLS_PER_PLAYER asks whether the players FIT. This asks whether the map
 * is big enough for an opening to exist at all, which is a different question
 * and the one that was measured:
 *
 *   36x36, 2 players, 10 runs   4  5  6  7  9 14 25 25 201 201   8/10 decided
 *   48x48, 2 players, 10 runs  11 12 22 23 25 30 32 41 148 201   9/10 decided
 *   72x72, 2 players, 10 runs  13 17 17 45 52 68 301 301 301 301 6/10 decided
 *
 * Identical code in all three. At 36x36 five games in ten are over inside ten
 * days; at 48x48 none are, and the distribution is the healthiest the balance
 * work has produced. The cause is not the separation rule - the capitals on
 * the short seeds were seated 23 to 28 cells apart, comfortably over the
 * floor - it is that at 36x36 even a COMPLIANT separation is under two days'
 * march, so the opening army can arrive before an opening exists. That cannot
 * be fixed from inside the map, and three rounds of tuning against it returned
 * progressively less.
 *
 * So 48 is the smallest map this generator will build. It is a deliberate
 * departure from the classic's own 36-cell Small: the original's balance tolerates
 * that distance and this one does not, and shipping a size whose games decide
 * themselves would be worse than shipping one fewer size.
 */
const MIN_MAP_CELLS = 48;

/**
 * The floor, in walking steps, under the gap between the two closest capitals.
 *
 * It is a share of what this map can actually deliver rather than a constant,
 * because a constant is unreachable on a 24x24 map and free on a 144x144 one.
 * `startRing` reports the Chebyshev gap between the two closest seats on the
 * best rotation of the start ring - which already scales with both the map's
 * size and the player count - and the town site then drifts a few cells inside
 * its own zone looking for six-by-four of dry level ground. Requiring the whole
 * ring gap would reject layouts for a drift the generator cannot undo;
 * `SEPARATION_BP` is the share that survives it, set from the measured
 * distribution of drift over 36x36 and 48x48 (worst observed: 88% of the gap).
 *
 * What the floor buys on the map the headless suite runs: 36x36 with two
 * players rings at a gap of 26, so the floor is 22 steps. A starting hero has
 * 1500 movement points, which is 15 steps of open grass or 23 of road, and the
 * generator lays a trunk road from capital to capital - so 22 steps is one full
 * day's march and no more. **That is all the geometry of a 36x36 map has to
 * give.** Separation buys the first day; the starting garrison has to buy the
 * rest, which is why both halves of this fix exist.
 */
const SEPARATION_BP = 8500;

export const requiredSeparation = (g: Grid, players: number): number =>
  Math.max(4, Math.trunc((startRing(g, players).gap * SEPARATION_BP) / 10000));

/** Steps to every reachable cell from `origin`, 8-way; -1 where unreachable. */
function stepField(m: AdventureMapState, g: Grid, origin: number): Int32Array {
  const dist = new Int32Array(g.width * g.height).fill(-1);
  dist[origin] = 0;
  const queue = [origin];
  const buf: number[] = [];
  for (let head = 0; head < queue.length; head++) {
    const cur = queue[head]!;
    const d = dist[cur]! + 1;
    for (const nb of allNeighbours(g, cur, buf)) {
      if (!walkable(m, nb) || dist[nb]! >= 0) continue;
      dist[nb] = d;
      queue.push(nb);
    }
  }
  return dist;
}

/**
 * Walking steps between the two closest town sites, on the map as it stands
 * before roads and objects.
 *
 * Steps rather than movement points on purpose. The trunk road laid in step 8
 * runs capital to capital and drops a step from 100 points to 65, so a points
 * measurement taken here would overstate the distance by a third - and it is
 * exactly the road-borne distance the rush travels. Steps measure the geometry,
 * and geometry is the only thing a retry can change.
 *
 * A site that is missing, or that cannot be walked to from another, returns 0:
 * both are broken layouts rather than distant ones, and both are better retried
 * than shipped.
 */
function startSeparation(
  m: AdventureMapState, g: Grid, sites: readonly (TownSite | null)[],
): number {
  let closest = 0x3fffffff;
  for (let a = 0; a < sites.length; a++) {
    const from = sites[a];
    if (from === null || from === undefined) return 0;
    const dist = stepField(m, g, idx(g, from.entX, from.entY));
    for (let b = a + 1; b < sites.length; b++) {
      const to = sites[b];
      if (to === null || to === undefined) return 0;
      const d = dist[idx(g, to.entX, to.entY)]!;
      if (d < 0) return 0;
      if (d < closest) closest = d;
    }
  }
  return closest;
}

/** Passes 2 to 4: terrain, zones, rivers and the player town sites. */
function buildLayout(
  g: Grid, r: Rng, players: number, waterBp: number,
  nativeOf: readonly TerrainKind[], shape: WaterShape,
): Layout {
  const map = createMap(g.width, g.height, 1, { terrain: "grass" });

  // ---- 2. zones and biomes
  const zoneCount = players * 3 + 4;
  const waterZones = clamp(Math.round((zoneCount * waterBp) / 10000), 1, zoneCount - players - 1);
  const neutralZones = Math.max(1, zoneCount - players - waterZones);
  const zones = growZones(map, r, players, neutralZones, waterZones, shape);
  const zoneOf = zoneMap(map, zones);
  const adjacency = zoneAdjacency(g, zoneOf, zones.length);
  assignTerrain(zones, adjacency, r, nativeOf);
  assignZoneJobs(g, zones, adjacency);
  paintZones(map, zones);
  erodeBoundaries(map, r, 2);
  despeckle(map);

  // Great rivers, before the seams and the beaches so their banks are painted
  // like any other coast, and before the mainland repair so a channel that cuts
  // the board in two comes back with a ford in it (waterways.ts).
  if (shape === "rivers") carveChannels(map, r, 2 + int(r, 2));

  smoothSeams(map);
  paintBeaches(map);

  // Every zone worth putting content in has to be on one landmass, or a town
  // ends up on an island and the whole map is a stalemate. Straits are bridged
  // with a sandbar rather than by shrinking the sea.
  const snapshot: Record<number, number> = {};
  for (let i = 0; i < map.terrain.length; i++) snapshot[i] = map.terrain[i]!;
  const mainland: number[] = [];
  // Player zones first: the flood roots at `mainland[0]`, and the continent a
  // player starts on is the one everything else has to reach.
  for (const pass of ["player", "neutral"] as const) {
    for (const z of zones) {
      if (z.terrain === "water" || z.role !== pass) continue;
      if (pass === "neutral" && z.cells.length < 60) continue;
      const rep = z.cells.find((i) => walkable(map, i));
      if (rep !== undefined) mainland.push(rep);
    }
  }
  repairConnectivity(map, mainland, snapshot);
  paintBeaches(map);

  // ---- 3. rivers, before anything can be standing in one
  let rivers = carveRivers(map, r, 2 + int(r, 3));

  // ---- 4. town sites; the ridge pass must not wall one in
  const playerZones = zones.filter((z) => z.role === "player")
    .sort((a, b) => a.player - b.player);
  const sites = playerZones.map((z) => findTownSite(map, r, z, zoneOf));
  // A site that could only be found across a river (eight players on a
  // 48-cell map leave no dry six-by-four anywhere else) takes the river out.
  for (const site of sites) if (site !== null) rivers = drySite(map, g, site, rivers);

  return {
    map, zones, zoneOf, adjacency, rivers, playerZones, sites,
    separation: startSeparation(map, g, sites),
  };
}

// --------------------------------------------------------------- decoration

function decorate(
  s: GenState, decorationBp: number, sceneryBp: number, rivers: readonly (readonly number[])[],
): Decoration[] {
  const out: Decoration[] = [];
  const { map: m, g } = s;
  const n = g.width * g.height;

  // Where the ground is open: on or beside a road, and on a town's apron. A
  // wood grows up to a road in the classic but not across its verge, and the cell a
  // hero steps out of a town onto is never a thicket.
  const open = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    if (m.road[i] === 0 && s.ctx.reserved[i] !== 1) continue;
    const x = xOf(g, i), y = yOf(g, i);
    for (let dy = -OPEN_APRON; dy <= OPEN_APRON; dy++) {
      for (let dx = -OPEN_APRON; dx <= OPEN_APRON; dx++) {
        if (inside(g, x + dx, y + dy)) open[idx(g, x + dx, y + dy)] = 1;
      }
    }
  }

  // Blocking scenery is a real object and passes the same connectivity gate as
  // everything else; a copse of trees may not cut a player off from a town.
  //
  // The chance is per cell but it is NOT the same everywhere, and that is the
  // whole of the difference between a wood and a sprinkle. A flat probability
  // is a Poisson process: no thicket, no clearing, no edge, the map equally
  // busy from corner to corner. `thicketScale` is a long-wavelength field with
  // a threshold in it, so about a fifth of the map carries four times the
  // scenery and about a third carries almost none, in blobs a dozen cells
  // across - which is a wood with a shape, and a meadow beside it.
  for (let i = 0; i < n; i++) {
    if (m.occupant[i] !== 0 || m.road[i] !== 0 || s.ctx.reserved[i] === 1) continue;
    if (!walkable(m, i)) continue;
    if (open[i] === 1) continue;
    const scale = thicketScale(xOf(g, i), yOf(g, i));
    if (int(s.r, 10000) * 1000 >= sceneryBp * scale) continue;
    const defId = pick(s.r, SCENERY[terrainOf(m, i)]?.blocking ?? []);
    if (defId !== null) place(s, [i], defId, nothing, 1);
  }

  // Decals get the same field flattened. Ground with no texture at all reads as
  // a bald patch rather than as a clearing, so a clearing keeps half its decals
  // where it keeps a tenth of its trees.
  for (let i = 0; i < n; i++) {
    if (m.occupant[i] !== 0 || m.road[i] !== 0) continue;
    const t = terrainOf(m, i);
    if (t === "rock") continue;
    if (int(s.r, 10000) * 1000 >= decorationBp * decalScale(xOf(g, i), yOf(g, i))) continue;
    const defId = pick(s.r, SCENERY[t]?.decals ?? []);
    if (defId !== null) out.push({ def: defId, x: xOf(g, i), y: yOf(g, i), z: 0 });
  }

  // A thin sprinkle of elemental fields, and a mouth where a river meets water.
  for (let i = 0; i < n; i++) {
    if (m.occupant[i] !== 0 || m.road[i] !== 0) continue;
    if (int(s.r, 10000) >= 60) continue;
    const defId = pick(s.r, FIELD_DECALS[terrainOf(m, i)] ?? []);
    if (defId !== null) out.push({ def: defId, x: xOf(g, i), y: yOf(g, i), z: 0 });
  }
  const RIVER_NAMES = ["none", "water", "ice", "lava", "mud"] as const;
  for (const river of rivers) {
    const mouth = river[river.length - 1];
    if (mouth === undefined) continue;
    const defId = DELTA_DECAL[RIVER_NAMES[m.river[mouth]!] ?? "water"];
    if (defId !== undefined) out.push({ def: defId, x: xOf(g, mouth), y: yOf(g, mouth), z: 0 });
  }
  out.sort((a, b) => (a.y - b.y) || (a.x - b.x) || (a.def < b.def ? -1 : a.def > b.def ? 1 : 0));
  return out;
}


// ------------------------------------------------------------------ assembly

/**
 * Copy the finished planes into one `levels`-deep map and re-stamp every
 * object footprint onto it.
 *
 * The occupancy plane is rebuilt rather than copied because `moveCost` is
 * derived from terrain, road *and* object blocking together, and the dense
 * `objectIndex` slot of an id is assigned on first use - copying the arrays
 * would carry over slots for objects the audit deleted.
 */
function assemble(
  width: number, height: number, planes: readonly AdventureMapState[],
  objects: readonly MapObjectState[], content: ContentRegistry,
): AdventureMapState {
  const world = createMap(width, height, planes.length, { terrain: "rock" });
  const n = width * height;
  planes.forEach((src, z) => {
    for (let i = 0; i < n; i++) {
      const x = i % width, y = Math.trunc(i / width);
      setTerrain(world, x, y, z, TERRAINS[src.terrain[i]!]!);
      setRoad(world, x, y, z, ROAD_KINDS[src.road[i]!]!);
      setRiver(world, x, y, z, RIVER_KINDS[src.river[i]!]!);
      setVariant(world, x, y, z, src.variant[i]!);
    }
  });
  for (const o of objects) {
    const def = content.mapObjects.get(o.def);
    if (def === undefined) continue;
    for (const [dx, dy] of def.footprint) {
      const blocks = def.blocksMovement
        && !def.entrances.some(([ex, ey]) => ex === dx && ey === dy);
      setOccupant(world, o.x + dx, o.y + dy, o.z, o.id, blocks);
    }
  }
  return world;
}

/**
 * Section 31 - scatter the magic ground.
 *
 * Patches, not regions: the classic lays a handful of small special grounds on a map
 * and a player learns where they are, which is what makes fighting on one a
 * decision. Measured against that, a 72x72 board gets about eight of them.
 *
 * The blob walk is deliberately crude - a random step from the patch's own
 * cells - because the shape wants to be ragged. What it will not do is cross
 * water or step onto a cell nothing can enter: a magic ground nobody can fight
 * on is a wash with no rule behind it.
 */
function paintMagicGround(world: AdventureMapState, r: Rng, content: ContentRegistry): void {
  const ids = [...content.magicGrounds.keys()].sort();
  if (ids.length === 0) return;

  const legend: (string | null)[] = [null];
  const slotOf = (id: string): number => {
    const at = legend.indexOf(id);
    if (at > 0) return at;
    legend.push(id);
    return legend.length - 1;
  };

  const { width, height, levels } = world;
  // One patch per ~650 land cells, which is eight on a 72x72 surface.
  const patches = Math.max(2, Math.round((width * height * levels) / 650));
  const ground = world.ground as Uint8Array;

  for (let p = 0; p < patches; p++) {
    const id = ids[int(r, ids.length)]!;
    const z = levels < 2 ? 0 : int(r, levels);
    const size = 5 + int(r, 12);
    let x = int(r, width), y = int(r, height);
    let placed = 0;
    for (let step = 0; step < size * 6 && placed < size; step++) {
      const i = indexOf(world, x, y, z);
      const open = i >= 0 && world.moveCost[i]! > 0 && !isWaterCell(world, i);
      if (open && ground[i]! === 0) { ground[i] = slotOf(id); placed++; }
      // Step to a neighbour whether or not that cell took the paint, so a
      // patch that starts on rock walks out of it instead of stalling there.
      x = clamp(x + int(r, 3) - 1, 0, width - 1);
      y = clamp(y + int(r, 3) - 1, 0, height - 1);
    }
  }
  (world as { groundIndex: readonly (string | null)[] }).groundIndex = legend;
}

/**
 * Resolve every town's index into the finished object list.
 *
 * Object indices are positional, and both the gate reconciliation and the audit
 * may delete an object that sits ahead of a town - and the underground's list is
 * concatenated onto the surface's - so the index is looked up against the final
 * list rather than remembered from placement time.
 */
function locateTowns(
  towns: readonly GeneratedTown[], objects: readonly MapObjectState[],
  content: ContentRegistry,
): GeneratedTown[] {
  return towns.map((t) => {
    const entrances = content.mapObjects.get(t.def)?.entrances ?? [];
    const at = objects.findIndex((o) => o.def === t.def && o.z === t.z
      && entrances.some(([dx, dy]) => o.x + dx === t.x && o.y + dy === t.y));
    return at === t.object ? t : { ...t, object: at };
  });
}
